import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import * as ep from '../api/endpoints';
import { Icon, type IconName } from '../components/Icon';
import { InstallBanner } from '../components/InstallBanner';
import { OfflineBanner } from '../components/OfflineBanner';
import { UpdatePrompt } from '../components/UpdatePrompt';
import { syncPush } from '../lib/push';
import { useAuth } from './auth';

interface NavItem { to: string; label: string; icon: IconName; end?: boolean; badge?: number }

const MAIN: NavItem[] = [
  { to: '/', label: 'Özet', icon: 'home', end: true },
  { to: '/havuz', label: 'Ortak havuz', icon: 'pool' },
  { to: '/fis-ekle', label: 'Fiş ekle', icon: 'plus' },
  { to: '/odemeler', label: 'Vadeli ödemeler', icon: 'calendar' },
  { to: '/istatistik', label: 'Gider istatistiği', icon: 'chart' },
  { to: '/arsiv', label: 'Arşiv', icon: 'archive' },
];

const MORE: NavItem[] = [
  { to: '/kisiler', label: 'Kişiler', icon: 'users' },
  { to: '/taslaklar', label: 'Taslaklarım', icon: 'draft' },
  { to: '/iptal-iade', label: 'İptaller / iadeler', icon: 'undo' },
];

/** Üst çubukta gösterilen sayfa adı (iOS gezinme çubuğu ve masaüstü başlığı). */
function titleFor(path: string): string {
  if (path === '/') return 'Özet';
  if (path.startsWith('/havuz')) return 'Ortak havuz';
  if (path.startsWith('/fis-ekle')) return 'Fiş ekle';
  if (path.startsWith('/fis/')) return 'Fiş';
  if (path.startsWith('/istatistik')) return 'Gider istatistiği';
  if (path.startsWith('/arsiv')) return 'Arşiv';
  if (path.startsWith('/kisiler')) return 'Kişiler';
  if (path.startsWith('/taslaklar')) return 'Taslaklarım';
  if (path.startsWith('/iptal-iade')) return 'İptaller / iadeler';
  if (path.startsWith('/yonetim')) return 'Yönetim';
  if (path.startsWith('/hesabim')) return 'Hesabım';
  if (path.startsWith('/odemeler')) return 'Vadeli ödemeler';
  if (path.startsWith('/bildirimler')) return 'Bildirimler';
  return 'aksanenerji';
}

const COLLAPSE_KEY = 'aksanenerji.sidebar.collapsed';

// yalnızca görünüm tercihi (kenar çubuğu daraltıldı mı); belirteç ya da özel veri değildir
function readCollapsed(): boolean {
  // eslint-disable-next-line no-restricted-syntax
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
}

/**
 * Uygulama iskeleti.
 *  - Telefon ve tablet (Apple arayüzü): sol üstteki üç çizgi menüyü açar; menü soldan kayan panel olarak gelir.
 *    Hızlı erişim için sağ altta "Fiş ekle" düğmesi durur.
 *  - Bilgisayar: menü solda sabit kenar çubuğudur; üç çizgi kenar çubuğunu daraltıp genişletir.
 */
export function Layout() {
  const { me, isAdmin, signOut } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const notif = useQuery({ queryKey: ['notifications'], queryFn: ep.notifications, refetchInterval: 60_000 });
  const unread = notif.data?.filter((n) => !n.read_at).length ?? 0;

  // bildirim izni verilmiş cihaz, giriş yapan kullanıcıya bağlanır (hesap değişiminde de)
  useEffect(() => { if (me) void syncPush(); }, [me?.user.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // sayfa değişince menü kapanır, sayfa başına dönülür
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);
  // menü açıkken Esc kapatır, arka plan kaymaz
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('no-scroll');
    return () => { document.removeEventListener('keydown', onKey); document.body.classList.remove('no-scroll'); };
  }, [open]);

  function toggleMenu() {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setCollapsed((c) => {
        // eslint-disable-next-line no-restricted-syntax
        try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* özel mod */ }
        return !c;
      });
    } else {
      setOpen((o) => !o);
    }
  }

  const admin: NavItem[] = isAdmin ? [{ to: '/yonetim', label: 'Yönetim', icon: 'shield' }] : [];
  const notifItem: NavItem = { to: '/bildirimler', label: 'Bildirimler', icon: 'bell', badge: unread };
  const firstName = me?.user.full_name.split(' ')[0] ?? '';
  const initials = (me?.user.full_name ?? '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toLocaleUpperCase('tr-TR')).join('');

  return (
    <div className={`shell${open ? ' menu-open' : ''}${collapsed ? ' collapsed' : ''}`}>
      <aside className="drawer" aria-label="Ana menü" id="ana-menu">
        <div className="drawer-brand">
          <img src="/logo.jpg" alt="" />
          <div className="drawer-brand-text">
            <strong>aksanenerji</strong>
            <span>Fiş ve gider takibi</span>
          </div>
          <button className="icon-btn drawer-close" onClick={() => setOpen(false)} aria-label="Menüyü kapat"><Icon name="close" /></button>
        </div>
        <nav className="drawer-nav">
          <div className="nav-group">
            {MAIN.map((n) => <MenuLink key={n.to} item={n} />)}
          </div>
          <div className="nav-group">
            <div className="nav-group-title">Diğer</div>
            {[notifItem, ...MORE, ...admin].map((n) => <MenuLink key={n.to} item={n} />)}
          </div>
        </nav>
        <div className="drawer-foot">
          <Link to="/hesabim" className="me-chip" title="Hesabım">
            <span className="avatar">{initials}</span>
            <span className="me-text"><strong>{me?.user.full_name}</strong><span>{isAdmin ? 'Yönetici' : 'Kullanıcı'}</span></span>
          </Link>
          <button className="icon-btn" onClick={() => void signOut()} aria-label="Çıkış yap" title="Çıkış yap"><Icon name="logout" /></button>
        </div>
      </aside>
      <div className="scrim" onClick={() => setOpen(false)} aria-hidden="true" />

      <div className="content">
        <header className="navbar">
          <button className="icon-btn hamburger" onClick={toggleMenu} aria-label="Menü" aria-controls="ana-menu" aria-expanded={open}>
            <Icon name="menu" size={24} />
            {unread > 0 && <span className="dot" aria-hidden="true" />}
          </button>
          <div className="navbar-title">
            <img src="/logo.jpg" alt="" className="navbar-logo" />
            <span className="navbar-brand">aksanenerji</span>
            <span className="navbar-page">{titleFor(pathname)}</span>
          </div>
          <span className="spacer" />
          <span className="navbar-hello">Merhaba, {firstName}</span>
          <Link to="/bildirimler" className="icon-btn bell-link" aria-label={unread ? `Bildirimler (${unread} okunmamış)` : 'Bildirimler'}>
            <Icon name="bell" size={22} />
            {unread > 0 && <span className="bell-count">{unread > 9 ? '9+' : unread}</span>}
          </Link>
          <Link to="/hesabim" className="avatar avatar-link" aria-label="Hesabım">{initials}</Link>
        </header>
        <OfflineBanner />
        <InstallBanner />
        <UpdatePrompt />
        <main className="main"><Outlet /></main>
      </div>

      {!pathname.startsWith('/fis-ekle') && (
        <Link to="/fis-ekle" className="fab" aria-label="Fiş ekle"><Icon name="plus" size={26} /><span>Fiş ekle</span></Link>
      )}
    </div>
  );
}

function MenuLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end={item.end} className="nav-item" title={item.label}>
      <span className="nav-ico"><Icon name={item.icon} /></span>
      <span className="nav-label">{item.label}</span>
      {!!item.badge && <span className="nav-badge">{item.badge}</span>}
      <Icon name="chevron" size={16} className="nav-chev" />
    </NavLink>
  );
}
